export type ConsentValue = 'granted' | 'denied';

export function categoriesToConsentMode(categories: string[]): Record<string, ConsentValue> {
  const analytics = categories.includes('analytics');
  const marketing = categories.includes('marketing');
  return {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  };
}

/**
 * Maps a raw `cc_cookie` value (the URL-encoded JSON vanilla-cookieconsent writes,
 * shape `{ categories: string[] }`) to the Google Consent Mode signals.
 *
 * IMPORTANT: the inline script in
 * resources/views/includes/head/consent-mode-default.blade.php reproduces this exact
 * logic synchronously before GTM loads (it cannot import this module). Any change to
 * the cc_cookie shape or category ids here MUST be mirrored there. This function is the
 * tested source of truth for that contract.
 */
export function consentModeDefaultFromCookie(rawCookie: string | null): Record<string, ConsentValue> {
  let categories: string[] = [];
  if (rawCookie) {
    try {
      categories = JSON.parse(decodeURIComponent(rawCookie)).categories || [];
    } catch {
      categories = [];
    }
  }
  return categoriesToConsentMode(categories);
}

export function updateConsentMode(categories: string[]): void {
  const gtag = (globalThis as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== 'function') {
    return;
  }
  gtag('consent', 'update', categoriesToConsentMode(categories));
}

/**
 * Didomi vendor-consent reconstruction.
 *
 * The live GTM container gates every advertising/analytics conversion trigger on
 * a filter of the form `{{DLV - Didomi Vendor Consent}} CONTAINS "didomi:google"`,
 * where that variable reads the `didomiVendorsConsent` dataLayer key. Didomi used
 * to push that key (a comma-joined list of consented vendor ids) on every consent
 * event; vanilla-cookieconsent does not, so after the CMP swap the variable stayed
 * empty and NONE of those conversion tags fired (Google Ads, GA4, Meta, Snap, Bing,
 * Criteo, Braze, Affilae, Hotjar, ActiveCampaign). We reproduce the signal from our
 * own consent categories so the existing triggers keep working, without editing the
 * shared GTM container.
 *
 * A vendor token is emitted ONLY when its category is granted, so this never signals
 * consent the user did not give. The value is a comma-joined string because the GTM
 * triggers match it with a substring CONTAINS.
 *
 * SYNC OBLIGATION: this map mirrors the `{{DLV - Didomi Vendor Consent}} CONTAINS "…"`
 * trigger conditions in GTM container GTM-5WTFG43 (account 4701872809). If a trigger
 * there starts requiring a new vendor token, add it here or that tag silently stops
 * firing. This coupling is temporary: INT-1385 Option B repoints the triggers onto a
 * CMP-native consent signal and removes this reconstruction entirely.
 */
const VENDOR_CONSENT_TOKENS: Record<'analytics' | 'marketing', readonly string[]> = {
  analytics: ['c:googleana-4TXnJigR', 'c:hotjar'],
  marketing: [
    'didomi:google',
    'facebook',
    'didomi:facebook',
    'iab:91',
    'iab:1000',
    'c:snapinc-yhYnJZfT',
    'c:microsoft',
    'activecampaign',
    'c:custom',
  ],
};

export function categoriesToVendorConsent(categories: string[]): string {
  const tokens: string[] = [];
  if (categories.includes('analytics')) {
    tokens.push(...VENDOR_CONSENT_TOKENS.analytics);
  }
  if (categories.includes('marketing')) {
    tokens.push(...VENDOR_CONSENT_TOKENS.marketing);
  }
  return tokens.join(',');
}

/**
 * Publishes the reconstructed Didomi vendor-consent string to the dataLayer so the
 * existing GTM conversion triggers (which read `didomiVendorsConsent`) fire again.
 * Pushed on every consent change AND on every reload for already-consented users
 * (onConsent fires on load), so the value is present before user-action conversion
 * events (Order Completed, Sign Up Completed, Add to Cart, ...) reach GTM.
 */
export function pushVendorConsent(categories: string[]): void {
  const w = globalThis as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ didomiVendorsConsent: categoriesToVendorConsent(categories) });
}
